import React, { useState, useEffect } from 'react';
import { KIND_LABELS, skillKindIcon, groupSkillsByKind } from '../constants/skillTaxonomy';

interface JobDetail {
  id: number;
  title: string;
  description: string | null;
  max_reward: number | null;
  min_reward: number | null;
  location_name: string | null;
  company_name: string | null;
  internal_job_listing_id: string;
  contract_type_name: string | null;
  workstyle_type: string | null;
  workstyle_type_label: string | null;
  prefecture: string | null;
  city: string | null;
  station: string | null;
  skills: Array<{ id: number; name: string; kind: string }>;
  occupations: Array<{ id: number; name: string }>;
  characteristics: Array<{ id: number; name: string }>;
  job_source: {
    id: number;
    name: string;
    display_name: string;
    corporate_name?: string;
    logo_image?: string;
    thumbnail_image?: string;
    payment_site?: string;
    affiliate_link?: string | null;
    partnership_type?: string;
  };
  required_skill: string | null;
  preferred_skill: string | null;
  development_environment: string | null;
  work_hours: string | null;
  settlement_hours: string | null;
  interview_count: number | null;
  payment_site: string | null;
  recruiter_comment: string | null;
  availability: string | null;
  availability_type: { key: string; name: string } | null;
  url: string | null;
  favorited: boolean;
  status: string;
  recommendation_points: string[] | null;
  breadcrumb_taxonomy: {
    type: 'skill' | 'occupation';
    id: number;
    name: string;
  } | null;
  cross_landing_pages: Array<{
    id: number;
    display_title: string;
    secondary_type: string;
  }>;
  related_job_listings: Array<{
    id: number;
    title: string;
    min_reward: number | null;
    max_reward: number | null;
    location_name: string | null;
    skills: Array<{ id: number; name: string }>;
    favorited: boolean;
  }>;
}

interface JobDetailPageProps {
  initialJobDetail: JobDetail;
  currentUserId?: number;
  isUserSignedIn: boolean;
  isProfileComplete: boolean;
  isServerRendered?: boolean;
}

const JobDetailPage: React.FC<JobDetailPageProps> = ({
  initialJobDetail,
  currentUserId,
  isUserSignedIn = false,
  isProfileComplete = false,
  isServerRendered = false
}) => {
  const [jobDetail, setJobDetail] = useState<JobDetail>(initialJobDetail);
  const [isFavorited, setIsFavorited] = useState(initialJobDetail.favorited);
  const [isLoading, setIsLoading] = useState(false);
  const [inquiryOptions, setInquiryOptions] = useState<number[]>([]);

  // Format number with thousands separators
  const formatNumber = (num: number): string => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const formatSalary = (min: number | null, max: number | null) => {
    if (min && max) {
      return `${formatNumber(min)}円〜${formatNumber(max)}円/月`;
    } else if (min) {
      return `${formatNumber(min)}円〜/月`;
    } else if (max) {
      return `〜${formatNumber(max)}円/月`;
    }
    return '単価要相談';
  };

  const formatAvailability = (availability: string | null, availabilityType: { key: string; name: string } | null) => {
    if (availabilityType) {
      return availabilityType.name;
    }
    if (availability) {
      if (/^\d+$/.test(availability)) {
        return `週${availability}日`;
      }
      return availability;
    }
    return '未設定';
  };

  const handleToggleFavorite = async () => {
    if (!isUserSignedIn) {
      // Show login modal or redirect
      const event = new CustomEvent('showLoginModal', { detail: { actionType: 'favorite' } });
      window.dispatchEvent(event);
      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

    try {
      if (isFavorited) {
        const response = await fetch(`/favorites/${jobDetail.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          }
        });

        if (response.ok) {
          setIsFavorited(false);
          setJobDetail({ ...jobDetail, favorited: false });
          if (window.showGlobalFlash) {
            window.showGlobalFlash('求人を保存から削除しました', 'success', false);
          }
        }
      } else {
        const response = await fetch('/favorites', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ job_listing_id: jobDetail.id })
        });

        if (response.ok) {
          setIsFavorited(true);
          setJobDetail({ ...jobDetail, favorited: true });
          if (window.showGlobalFlash) {
            window.showGlobalFlash('求人を保存しました', 'success', false);
          }
        }
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      if (window.showGlobalFlash) {
        window.showGlobalFlash('エラーが発生しました。再度お試しください。', 'error', false);
      }
    }
  };

  const handleApply = () => {
    if (jobDetail.status === 'closed') {
      return;
    }

    if (jobDetail.job_source.partnership_type === 'affiliate') {
      const url = jobDetail.job_source.affiliate_link || jobDetail.url;
      if (url) {
        window.open(url, '_blank', 'noopener');
      }
      return;
    }

    if (!isUserSignedIn) {
      const params = new URLSearchParams({
        job_listing_id: jobDetail.id.toString(),
        action_type: 'apply',
        job_title: jobDetail.title,
        job_source: jobDetail.job_source.display_name || '',
        company_name: jobDetail.company_name || ''
      });
      window.location.href = `/apply/sign_up?${params.toString()}`;
      return;
    }

    if (!isProfileComplete) {
      window.location.href = '/registration/step1';
      return;
    }

    // Check if already applied
    fetch(`/api/v1/pending_job_application?job_listing_id=${jobDetail.id}`, {
      headers: { 'Accept': 'application/json' }
    })
      .then(res => res.json())
      .then(data => {
        if (data.applied) {
          if (window.showGlobalFlash) {
            window.showGlobalFlash('既に応募済みです', 'info', false);
          }
          return;
        }

        // Submit application form
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `/job_listings/${jobDetail.id}/apply`;

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'authenticity_token';
        csrfInput.value = csrfToken;
        form.appendChild(csrfInput);

        document.body.appendChild(form);
        form.submit();
      })
      .catch(error => {
        console.error('Failed to check application status:', error);
        // Proceed with application anyway
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `/job_listings/${jobDetail.id}/apply`;

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'authenticity_token';
        csrfInput.value = csrfToken;
        form.appendChild(csrfInput);

        document.body.appendChild(form);
        form.submit();
      });
  };

  const toggleInquiryOption = (optionId: number) => {
    setInquiryOptions(prev => 
      prev.includes(optionId) 
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const skillsGrouped = groupSkillsByKind(jobDetail.skills);
  const isClosed = jobDetail.status === 'closed';

  return (
    <div className="min-vh-100 auth-background">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 p-2 p-md-4">
            <div className="card h-100 d-flex flex-column" style={{ maxWidth: '800px', margin: '0 auto' }}>
              <div className="card-body flex-grow-1 overflow-auto px-4 py-5">
                
                {/* Title */}
                <div className="mb-3">
                  <h1 
                    className="card-title mb-0 fw-bold" 
                    style={{ 
                      fontSize: '1.5rem', 
                      lineHeight: 1.6, 
                      whiteSpace: 'pre-line', 
                      wordBreak: 'break-word', 
                      overflowWrap: 'break-word' 
                    }}
                  >
                    {jobDetail.title}
                  </h1>
                </div>

                {/* Characteristics Tags */}
                {jobDetail.characteristics && jobDetail.characteristics.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-1">
                      {jobDetail.characteristics.slice(0, 3).map((characteristic) => (
                        <a
                          key={characteristic.id}
                          href={`/job_listings/characteristics/${characteristic.id}`}
                          className="badge bg-primary text-decoration-none"
                        >
                          {characteristic.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendation Points */}
                {jobDetail.recommendation_points && jobDetail.recommendation_points.length > 0 && (
                  <div 
                    className="mb-4 p-4 rounded" 
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(25, 135, 84, 0.1) 0%, rgba(25, 135, 84, 0.05) 100%)' 
                    }}
                  >
                    <h6 className="fw-bold text-success mb-2">
                      <i className="fas fa-star me-2 mb-4"></i>この求人の特徴とおすすめの人
                    </h6>
                    <ul className="list-unstyled mb-0">
                      {jobDetail.recommendation_points.map((point, index) => (
                        <li key={index} className="mb-2 d-flex align-items-start">
                          <i className="fas fa-check-circle text-success me-2 mt-1" style={{ fontSize: '0.8rem' }}></i>
                          <span 
                            style={{ lineHeight: 1.5 }}
                            dangerouslySetInnerHTML={{ 
                              __html: point.replace(/<strong>/g, '<span class="highlight-yellow">').replace(/<\/strong>/g, '</span>')
                            }}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <style>{`
                  .highlight-yellow {
                    background-color: #fff3cd;
                    color: #856404;
                    padding: 2px 4px;
                    border-radius: 3px;
                    font-weight: 600;
                  }
                `}</style>

                {/* Basic Info Summary */}
                <hr className="my-4" />
                <div className="mb-4">
                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-yen-sign me-2 text-muted" style={{ width: '16px', textAlign: 'center' }}></i>
                        <div>
                          <div className="fw-bold text-danger" style={{ fontSize: '1.1rem' }}>
                            {formatSalary(jobDetail.min_reward, jobDetail.max_reward)}
                          </div>
                        </div>
                      </div>
                    </div>
                    {jobDetail.contract_type_name && (
                      <div className="col-md-3 mb-2">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-briefcase me-2 text-muted" style={{ width: '16px', textAlign: 'center' }}></i>
                          <div>
                            <div>{jobDetail.contract_type_name}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {jobDetail.location_name && jobDetail.location_name !== '未設定' && (
                      <div className="col-md-3 mb-2">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-map-marker-alt me-2 text-muted" style={{ width: '16px', textAlign: 'center' }}></i>
                          <div>
                            <div>{jobDetail.location_name}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {jobDetail.workstyle_type_label && (
                      <div className="col-md-3 mb-2">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-laptop me-2 text-muted" style={{ width: '16px', textAlign: 'center' }}></i>
                          <div>
                            <div>{jobDetail.workstyle_type_label}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <hr className="my-4" />

                {/* Action Buttons */}
                <div className="my-5">
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                      style={{
                        backgroundColor: isFavorited ? undefined : 'white',
                        color: isFavorited ? '#dc3545' : '#00bb94',
                        borderColor: isFavorited ? '#dc3545' : '#00bb94',
                        minWidth: 'auto',
                        flexShrink: 1
                      }}
                      onClick={handleToggleFavorite}
                    >
                      <i className={isFavorited ? 'fas fa-bookmark' : 'far fa-bookmark'}></i>
                      <span className="d-none d-sm-inline ms-1">{isFavorited ? '求人を保存済み' : '求人を保存する'}</span>
                    </button>
                    {isClosed ? (
                      <button type="button" className="btn btn-primary" style={{ flex: '1 1 0%', minWidth: 0 }} disabled>
                        <i className="bi bi-slash-circle me-2"></i>募集終了
                      </button>
                    ) : jobDetail.job_source.partnership_type === 'direct' ? (
                      isUserSignedIn ? (
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ flex: '1 1 0%', minWidth: 0 }}
                          onClick={handleApply}
                        >
                          <i className="bi bi-arrow-right-circle me-2"></i>応募する
                        </button>
                      ) : (
                        <a
                          href={`/apply/sign_up?job_listing_id=${jobDetail.id}&action_type=apply&job_title=${encodeURIComponent(jobDetail.title)}&job_source=${encodeURIComponent(jobDetail.job_source.display_name || '')}&company_name=${encodeURIComponent(jobDetail.company_name || '')}`}
                          className="btn btn-primary"
                          style={{ flex: 1 }}
                          rel="nofollow"
                        >
                          <i className="bi bi-arrow-right-circle me-2"></i>この案件について相談する
                        </a>
                      )
                    ) : jobDetail.job_source.partnership_type === 'affiliate' ? (
                      <a
                        href={jobDetail.job_source.affiliate_link || jobDetail.url || '#'}
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                        target="_blank"
                        rel="noopener"
                      >
                        <i className="bi bi-box-arrow-up-right me-2"></i>この案件について相談する
                      </a>
                    ) : (
                      <a
                        href={jobDetail.url || '#'}
                        className="btn btn-primary"
                        target="_blank"
                        rel="noopener"
                      >
                        <i className="bi bi-box-arrow-up-right me-2"></i>この案件について相談する
                      </a>
                    )}
                  </div>
                </div>

                {/* Occupations */}
                {jobDetail.occupations && jobDetail.occupations.length > 0 && (
                  <div className="row mb-2">
                    <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">職種・ポジション</h3>
                    <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                      <div className="d-flex flex-wrap gap-1">
                        {jobDetail.occupations.map((occupation) => (
                          <a
                            key={occupation.id}
                            href={`/job_listings/occupations/${occupation.id}`}
                            className="badge bg-primary text-decoration-none"
                          >
                            {occupation.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Job Description */}
                <div className="row mb-2">
                  <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">作業内容</h3>
                  <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                    <p className="mb-0" style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                      {jobDetail.description || ''}
                    </p>
                  </div>
                </div>

                {/* Skills */}
                {Object.keys(skillsGrouped).length > 0 && (
                  <div className="row mb-2">
                    <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">開発環境</h3>
                    <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                      {Object.entries(skillsGrouped).map(([kind, skills]) => (
                        <div key={kind} className="mb-3 d-flex align-items-center flex-wrap gap-2">
                          <div 
                            className="fw-bold text-dark d-flex align-items-center" 
                            style={{ fontSize: '0.9rem', minWidth: 'fit-content' }}
                          >
                            <i className={`${skillKindIcon(kind)} me-1`}></i>
                            {KIND_LABELS[kind] || kind}:
                          </div>
                          <div className="d-flex flex-wrap gap-1">
                            {skills.map((skill) => (
                              <a
                                key={skill.id}
                                href={`/job_listings/skills/${skill.id}`}
                                className="badge bg-primary text-decoration-none"
                              >
                                {skill.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Required Skills */}
                {jobDetail.required_skill && (
                  <div className="row mb-2">
                    <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">必須スキル</h3>
                    <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                      <p className="mb-0" style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                        {jobDetail.required_skill}
                      </p>
                    </div>
                  </div>
                )}

                {/* Preferred Skills */}
                {jobDetail.preferred_skill && (
                  <div className="row mb-2">
                    <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">歓迎スキル</h3>
                    <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                      <p className="mb-0" style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                        {jobDetail.preferred_skill}
                      </p>
                    </div>
                  </div>
                )}

                {/* Availability */}
                {(jobDetail.availability || jobDetail.availability_type) && (
                  <div className="row mb-2">
                    <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">稼働日数</h3>
                    <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                      <p className="mb-0">
                        {formatAvailability(jobDetail.availability, jobDetail.availability_type)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Work Style */}
                {jobDetail.workstyle_type_label && (
                  <div className="row mb-2">
                    <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">勤務形態</h3>
                    <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                      <p className="mb-0">{jobDetail.workstyle_type_label}</p>
                    </div>
                  </div>
                )}

                {/* Location */}
                {(jobDetail.location_name || jobDetail.prefecture || jobDetail.city || jobDetail.station) && (
                  <div className="row mb-2">
                    <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">勤務地</h3>
                    <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                      <p className="mb-0">
                        {[
                          jobDetail.location_name,
                          jobDetail.prefecture,
                          jobDetail.city,
                          jobDetail.station
                        ].filter(Boolean).join(' ')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Interview Count */}
                {jobDetail.interview_count && (
                  <div className="row mb-2">
                    <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">面談回数</h3>
                    <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                      <p className="mb-0">{jobDetail.interview_count}回</p>
                    </div>
                  </div>
                )}

                {/* Work Hours */}
                {jobDetail.work_hours && (
                  <div className="row mb-2">
                    <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">精算基準時間</h3>
                    <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                      <p className="mb-0">{jobDetail.work_hours}</p>
                    </div>
                  </div>
                )}

                {/* Development Environment */}
                {jobDetail.development_environment && (
                  <div className="row mb-2">
                    <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">作業時間</h3>
                    <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                      <p className="mb-0">{jobDetail.development_environment}</p>
                    </div>
                  </div>
                )}

                {/* Application Count */}
                <div className="row mb-2">
                  <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">募集回数</h3>
                  <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                    <p className="mb-0">1回</p>
                  </div>
                </div>

                {/* Recruiter Comment */}
                {jobDetail.recruiter_comment && (
                  <div className="row mb-2">
                    <h3 className="col-md-3 bg-light py-3 px-4 text-muted fw-bold mb-0">募集背景</h3>
                    <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                      <p className="mb-0" style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                        {jobDetail.recruiter_comment}
                      </p>
                    </div>
                  </div>
                )}

                {/* CTA Warning Box */}
                <div className="mb-4 mt-4">
                  <div className="alert alert-warning" role="alert">
                    <div className="text-center">
                      <div className="mb-2">3件以上の応募でご希望の条件の案件に参画しやすくなります</div>
                      <div>人気案件は申し込みが集中します。ご相談はお早めに！</div>
                    </div>
                  </div>
                  
                  <div className="d-flex gap-3 mt-3">
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                      style={{
                        backgroundColor: isFavorited ? undefined : 'white',
                        color: isFavorited ? '#dc3545' : '#00bb94',
                        borderColor: isFavorited ? '#dc3545' : '#00bb94',
                        minWidth: 'auto',
                        flexShrink: 1
                      }}
                      onClick={handleToggleFavorite}
                    >
                      <i className={isFavorited ? 'fas fa-bookmark' : 'far fa-bookmark'}></i>
                      <span className="d-none d-sm-inline ms-1">{isFavorited ? '求人を保存済み' : '求人を保存する'}</span>
                    </button>
                    
                    {isClosed ? (
                      <button type="button" className="btn btn-primary" style={{ flex: '1 1 0%', minWidth: 0 }} disabled>
                        <i className="bi bi-slash-circle me-2"></i>募集終了
                      </button>
                    ) : jobDetail.job_source.partnership_type === 'direct' ? (
                      isUserSignedIn ? (
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ flex: '1 1 0%', minWidth: 0 }}
                          onClick={handleApply}
                        >
                          <i className="bi bi-arrow-right-circle me-2"></i>応募する
                        </button>
                      ) : (
                        <a
                          href={`/apply/sign_up?job_listing_id=${jobDetail.id}&action_type=apply&job_title=${encodeURIComponent(jobDetail.title)}&job_source=${encodeURIComponent(jobDetail.job_source.display_name || '')}&company_name=${encodeURIComponent(jobDetail.company_name || '')}`}
                          className="btn btn-primary"
                          style={{ flex: 1 }}
                          rel="nofollow"
                        >
                          <i className="bi bi-arrow-right-circle me-2"></i>この案件について相談する
                        </a>
                      )
                    ) : jobDetail.job_source.partnership_type === 'affiliate' ? (
                      <a
                        href={jobDetail.job_source.affiliate_link || jobDetail.url || '#'}
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                        target="_blank"
                        rel="noopener"
                      >
                        <i className="bi bi-box-arrow-up-right me-2"></i>この案件について相談する
                      </a>
                    ) : (
                      <a
                        href={jobDetail.url || '#'}
                        className="btn btn-primary"
                        target="_blank"
                        rel="noopener"
                      >
                        <i className="bi bi-box-arrow-up-right me-2"></i>この案件について相談する
                      </a>
                    )}
                  </div>
                </div>

                {/* Job Source Information */}
                <div className="mb-4 mt-5">
                  <hr className="mb-4" />
                  <h5 className="mb-3 fw-bold">この案件を掲載しているエージェントについて</h5>
                  <a 
                    href={`/job_sources/${jobDetail.job_source.id}`}
                    className="text-decoration-none"
                    style={{ display: 'block' }}
                  >
                    <div 
                      className="card"
                      style={{ 
                        transition: 'box-shadow 0.2s ease-in-out',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '';
                      }}
                    >
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-2">
                          <div className="me-3">
                            {jobDetail.job_source.logo_image ? (
                              <img
                                src={jobDetail.job_source.logo_image}
                                alt={jobDetail.job_source.name}
                                style={{ width: '60px', height: '40px', objectFit: 'contain' }}
                                className="rounded"
                              />
                            ) : (
                              <div 
                                className="bg-secondary rounded d-flex align-items-center justify-content-center"
                                style={{ width: '60px', height: '40px' }}
                              >
                                <i className="fas fa-building text-white"></i>
                              </div>
                            )}
                          </div>
                          <div>
                            <h6 className="mb-1 fw-bold" style={{ color: '#00bb94' }}>
                              {jobDetail.job_source.display_name}
                            </h6>
                            <small className="text-muted">
                              運営会社：{jobDetail.job_source.corporate_name || jobDetail.job_source.name}
                            </small>
                          </div>
                        </div>
                        <div className="bg-light rounded p-3 mt-3">
                          <div className="mb-2">
                            <small className="fw-bold text-dark">支払いサイト：</small>
                            <span className="text-muted">
                              {jobDetail.job_source.payment_site || '非公開'}
                            </span>
                          </div>
                          <div>
                            <small className="fw-bold text-dark">契約形態：</small>
                            <span className="text-muted">
                              {jobDetail.contract_type_name || '業務委託(フリーランス)'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>

                {/* Job Inquiry Form - Only show for guest users */}
                {!isUserSignedIn && (
                  <div className="mb-4 mt-5">
                    <div 
                      className="card"
                      style={{
                        backgroundColor: '#f8f9ff',
                        border: '1px solid #e3e8ff',
                        borderRadius: '12px'
                      }}
                    >
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <h5 className="card-title mb-0 fw-bold text-dark" style={{ fontSize: '1.1rem' }}>
                            案件について詳しく聞いてみませんか？
                          </h5>
                          <div className="d-flex align-items-center" style={{ fontSize: '1.5rem' }}>
                            <i className="fas fa-comments text-primary"></i>
                          </div>
                        </div>

                        <hr className="my-3" style={{ borderColor: '#e3e8ff' }} />

                        <p className="text-muted small mb-3">
                          知りたい内容を選んでください（複数選択可）
                        </p>

                        <div className="mb-4">
                          {[
                            { id: 1, label: 'フリーランス案件と正社員求人の違いを知りたい' },
                            { id: 2, label: '自分の経験に基づいた単価相場を知りたい' },
                            { id: 3, label: 'リモートワークで参加可能な案件を探したい' },
                            { id: 4, label: '自分のスキルに合った案件を探したい' },
                            { id: 5, label: 'プロジェクトでの裁量権の範囲を知りたい' }
                          ].map((option) => (
                            <div
                              key={option.id}
                              className="form-check mb-3"
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleInquiryOption(option.id)}
                            >
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`inquiry-${option.id}`}
                                checked={inquiryOptions.includes(option.id)}
                                onChange={() => toggleInquiryOption(option.id)}
                                style={{ transform: 'scale(1.2)', marginTop: '0.1rem', cursor: 'pointer' }}
                              />
                              <label
                                className="form-check-label ms-3"
                                htmlFor={`inquiry-${option.id}`}
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '0.95rem',
                                  lineHeight: 1.5,
                                  fontWeight: 500
                                }}
                              >
                                {option.label}
                              </label>
                            </div>
                          ))}
                        </div>

                        <div className="text-center">
                          <a
                            href={`/apply/sign_up?job_listing_id=${jobDetail.id}&action_type=apply&job_title=${encodeURIComponent(jobDetail.title)}&job_source=${encodeURIComponent(jobDetail.job_source.display_name || '')}&company_name=${encodeURIComponent(jobDetail.company_name || '')}`}
                            className="btn btn-primary px-4 py-2 fw-semibold"
                            style={{
                              backgroundColor: '#0d6efd',
                              borderColor: '#0d6efd',
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              minWidth: '200px'
                            }}
                            rel="nofollow"
                          >
                            詳しく聞いてみる
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Related Job Listings */}
                {jobDetail.related_job_listings && jobDetail.related_job_listings.length > 0 && (
                  <div className="mb-3 mb-md-4">
                    <h4 className="mb-3 mb-md-4 h5 h4-md fw-bold">関連する求人</h4>
                    
                    {/* Mobile: Simple scroll */}
                    <div className="d-block d-md-none">
                      <div 
                        className="d-flex gap-2 overflow-auto pb-3"
                        style={{
                          scrollSnapType: 'x mandatory',
                          WebkitOverflowScrolling: 'touch',
                          scrollbarWidth: 'none'
                        }}
                      >
                        {jobDetail.related_job_listings.slice(0, 8).map((job) => (
                          <div key={job.id} className="flex-shrink-0" style={{ width: '280px', scrollSnapAlign: 'start' }}>
                            <div 
                              className="card h-100 p-3"
                              style={{ minHeight: '120px', transition: 'all 0.2s ease' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '';
                                e.currentTarget.style.transform = '';
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1 me-3">
                                  <h6 className="card-title mb-2 fw-bold" style={{ fontSize: '14px', lineHeight: 1.4 }}>
                                    <a
                                      href={`/job_listings/${job.id}`}
                                      style={{ textDecoration: 'none', color: 'inherit' }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#00b894';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = 'inherit';
                                      }}
                                    >
                                      {job.title.length > 60 ? `${job.title.substring(0, 60)}...` : job.title}
                                    </a>
                                  </h6>
                                  
                                  <div className="mb-2" style={{ color: '#dc2626', fontWeight: 600, fontSize: '14px' }}>
                                    {formatSalary(job.min_reward, job.max_reward)}
                                  </div>
                                  
                                  <div className="d-flex flex-wrap gap-2 small text-muted">
                                    {job.location_name && job.location_name !== '未設定' && (
                                      <span>
                                        <i className="bi bi-geo-alt me-1"></i>{job.location_name}
                                      </span>
                                    )}
                                    {job.skills && job.skills.length > 0 && (
                                      <span>
                                        {job.skills[0].name}
                                        {job.skills.length > 1 && `他${job.skills.length - 1}件`}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Desktop: 1行に1つの横長レイアウト */}
                    <div className="d-none d-md-block">
                      <div className="row g-3">
                        {jobDetail.related_job_listings.map((job) => (
                          <div key={job.id} className="col-12">
                            <div 
                              className="card h-100 p-4"
                              style={{ transition: 'all 0.2s ease' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '';
                                e.currentTarget.style.transform = '';
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="flex-grow-1 me-4">
                                  <h5 className="card-title mb-2 fw-bold" style={{ fontSize: '16px', lineHeight: 1.4 }}>
                                    <a
                                      href={`/job_listings/${job.id}`}
                                      style={{ textDecoration: 'none', color: 'inherit' }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#00b894';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = 'inherit';
                                      }}
                                    >
                                      {job.title}
                                    </a>
                                  </h5>
                                  
                                  <div className="d-flex flex-wrap gap-3 align-items-center">
                                    <div style={{ color: '#dc2626', fontWeight: 600, fontSize: '16px' }}>
                                      {formatSalary(job.min_reward, job.max_reward)}
                                    </div>
                                    
                                    {job.location_name && job.location_name !== '未設定' && (
                                      <span className="text-muted">
                                        <i className="bi bi-geo-alt me-1"></i>{job.location_name}
                                      </span>
                                    )}
                                    
                                    {job.skills && job.skills.length > 0 && (
                                      <div className="d-flex flex-wrap gap-1">
                                        {job.skills.slice(0, 3).map((skill) => (
                                          <span key={skill.id} className="badge bg-primary" style={{ fontSize: '11px' }}>
                                            {skill.name}
                                          </span>
                                        ))}
                                        {job.skills.length > 3 && (
                                          <span className="badge bg-secondary" style={{ fontSize: '11px' }}>
                                            他{job.skills.length - 3}件
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="d-flex align-items-center">
                                  <a href={`/job_listings/${job.id}`} className="btn btn-primary btn-sm">
                                    <i className="bi bi-arrow-right-circle"></i>
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Cross Landing Pages Section */}
                {(jobDetail.skills.length > 0 || jobDetail.occupations.length > 0) && (
                  <div className="mb-4 mt-4">
                    <hr className="mb-4" />
                    <h5 className="mb-3 fw-bold">かけ合わせ条件で案件を探す</h5>
                    
                    {jobDetail.cross_landing_pages && jobDetail.cross_landing_pages.length > 0 ? (
                      <div className="row g-2">
                        {jobDetail.cross_landing_pages
                          .filter(lp => lp.secondary_type === 'Occupation')
                          .slice(0, 3)
                          .map((lp) => (
                            <div key={lp.id} className="col-6 col-md-4">
                              <a
                                href={`/job_listings/cross/${lp.id}`}
                                className="btn btn-outline-primary btn-sm w-100 text-start"
                              >
                                <i className="bi bi-search me-1"></i>
                                <small>{lp.display_title}</small>
                              </a>
                            </div>
                          ))}
                        {jobDetail.cross_landing_pages
                          .filter(lp => lp.secondary_type === 'Characteristic')
                          .slice(0, 3)
                          .map((lp) => (
                            <div key={lp.id} className="col-6 col-md-4">
                              <a
                                href={`/job_listings/cross/${lp.id}`}
                                className="btn btn-outline-primary btn-sm w-100 text-start"
                              >
                                <i className="bi bi-tag me-1"></i>
                                <small>{lp.display_title}</small>
                              </a>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="row g-2">
                        {jobDetail.skills.length > 0 && jobDetail.occupations.length > 0 && (
                          <>
                            {jobDetail.occupations.slice(0, 3).map((occupation) => {
                              const primarySkill = jobDetail.skills[0];
                              if (!primarySkill) return null;
                              return (
                                <div key={occupation.id} className="col-6 col-md-4">
                                  <a
                                    href={`/job_listings/search?skill_ids[]=${primarySkill.id}&occupation_ids[]=${occupation.id}`}
                                    className="btn btn-outline-primary btn-sm w-100 text-start"
                                    rel="nofollow"
                                  >
                                    <i className="bi bi-search me-1"></i>
                                    <small>{primarySkill.name} × {occupation.name}</small>
                                  </a>
                                </div>
                              );
                            })}
                          </>
                        )}
                        {jobDetail.skills.length > 0 && jobDetail.characteristics.length > 0 && (
                          <>
                            {jobDetail.characteristics.slice(0, 3).map((characteristic) => {
                              const primarySkill = jobDetail.skills[0];
                              if (!primarySkill) return null;
                              return (
                                <div key={characteristic.id} className="col-6 col-md-4">
                                  <a
                                    href={`/job_listings/search?skill_ids[]=${primarySkill.id}&characteristic_ids[]=${characteristic.id}`}
                                    className="btn btn-outline-primary btn-sm w-100 text-start"
                                    rel="nofollow"
                                  >
                                    <i className="bi bi-tag me-1"></i>
                                    <small>{primarySkill.name} × {characteristic.name}</small>
                                  </a>
                                </div>
                              );
                            })}
                          </>
                        )}
                        {jobDetail.skills.length > 0 && (
                          <>
                            <div className="col-6 col-md-4">
                              <a
                                href={`/job_listings/search?skill_ids[]=${jobDetail.skills[0].id}&remote_types[]=remote&remote_types[]=full_remote`}
                                className="btn btn-outline-primary btn-sm w-100 text-start"
                                rel="nofollow"
                              >
                                <i className="bi bi-house me-1"></i>
                                <small>{jobDetail.skills[0].name} × リモート</small>
                              </a>
                            </div>
                            <div className="col-6 col-md-4">
                              <a
                                href={`/job_listings/search?skill_ids[]=${jobDetail.skills[0].id}&price_over=800000`}
                                className="btn btn-outline-primary btn-sm w-100 text-start"
                                rel="nofollow"
                              >
                                <i className="bi bi-currency-yen me-1"></i>
                                <small>{jobDetail.skills[0].name} × 高単価</small>
                              </a>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailPage;

